/* eslint-disable react/function-component-definition, react/destructuring-assignment, react/prop-types */
import React, { useEffect, useMemo, useState } from 'react'
import { Checkbox } from '../Checkbox'
import './Table.module.scss'

function Selection(props) {
  const { checked, disabled, indeterminate } = props // eslint-disable-line react/prop-types
  return <Checkbox readOnly checked={checked} disabled={disabled} indeterminate={indeterminate} />
}

// react-bootstrap-table-next's SelectionCell only re-renders a row when its OWN `disabled` prop
// (the value it computes from `nonSelectable`) — or `selected`/`rowIndex`/`rowKey`/`tabIndex`/
// `selectColumnStyle` — changes; it has no way to know a *custom* selectionRenderer closed over a
// `loading` value that later changed. Baking `loading` into `disabled` only inside the renderer
// (as this used to do) left most rows frozen on whatever `disabled` they first rendered with —
// permanently greyed-out-looking checkboxes for rows whose real `disabled` prop never changed,
// even once loading finished. Folding `loading` into `nonSelectable` instead (below) means the
// library's own tracked `disabled` prop changes, so it re-renders every row correctly.
const selectionRenderer = ({ disabled, ...rest }) => <Selection {...rest} disabled={disabled} />

const onSelect =
  ({ keyField, externalOnSelect, selectedData, setSelectedData }) =>
  (row, isSelect) => {
    const result = isSelect ? selectedData.concat(row) : selectedData.filter((item) => item[keyField] !== row[keyField])

    setSelectedData(result)
    externalOnSelect(result)
  }

const onSelectAll =
  ({ keyField, externalOnSelect, allData, nonSelectable, setSelectedData }) =>
  (isSelect) => {
    const result = isSelect ? allData.filter((row) => !nonSelectable.includes(row[keyField])) : []
    setSelectedData(result)
    externalOnSelect(result)
    // returning keys causes the library to set _this.selected synchronously instead of via CWRP
    return result.map((row) => row[keyField])
  }

const useSelectRow = ({
  keyField,
  externalOnSelect,
  selected,
  nonSelectable,
  loading,
  readOnly,
  allData,
  renderedData,
}) => {
  const [selectedData, setSelectedData] = useState([])

  // Every row must render disabled while loading — routed through `nonSelectable` (see
  // `selectionRenderer` above) rather than a condition inside the renderer, so the library's own
  // per-row memoization actually notices and re-renders once loading finishes. Keyed off
  // `renderedData` (what the table actually renders, including skeleton placeholder rows when
  // there's no data yet) rather than `allData` (the real data, which is empty during that
  // skeleton state) — otherwise the placeholder rows' keys are missing from this list and their
  // checkboxes render enabled.
  const effectiveNonSelectable = loading ? renderedData.map((row) => row[keyField]) : nonSelectable

  const result = useMemo(
    () => ({
      selected: selectedData.map((item) => item[keyField]),
      onSelect: onSelect({
        keyField,
        externalOnSelect,
        selectedData,
        setSelectedData,
      }),
      // "Select all" should only ever exclude the genuinely non-selectable rows, never the
      // loading-inflated list — it can't be triggered while loading anyway.
      onSelectAll: onSelectAll({ keyField, externalOnSelect, allData, nonSelectable, setSelectedData }),
      nonSelectable: effectiveNonSelectable,
      mode: 'checkbox',
      classes: 'Uic-Table-selected',
      selectionRenderer,
      selectionHeaderRenderer: selectionRenderer,
    }),
    [selectedData, keyField, externalOnSelect, effectiveNonSelectable, nonSelectable, allData],
  )

  useEffect(() => {
    if (Array.isArray(selected)) {
      setSelectedData(selected)
    }
  }, [selected])

  // for the row selection to be enabled user has to pass onSelect to Table
  return !readOnly && externalOnSelect ? result : undefined
}

export default useSelectRow
